import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Autocomplete, TextField, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../App';
import * as XLSX from 'xlsx';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';

const ServiceClaimReport = () => {
    const [reportType, setReportType] = useState(null);
    const [branchList, setBranchList] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [dealerList, setDealerList] = useState([]);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const login_id = localStorage.getItem("LOGIN_ID");
    const BRANCH_CODE = localStorage.getItem("BRANCH_CODE");

    const months = Array.from({ length: 12 }, (_, i) => ({
        label: new Date(0, i).toLocaleString('en', { month: 'short' }),
        value: (i + 1).toString()
    }));

    const years = Array.from({ length: 10 }, (_, i) => ({
        label: `${new Date().getFullYear() - i}`,
        value: `${new Date().getFullYear() - i}`
    }));

    const reportTypes = [
        { label: 'Pending', value: 'P' },
        { label: 'Accounted', value: 'A' },
        { label: 'Rejected', value: 'R' }
    ];

    useEffect(() => {
        fetchBranches();
    }, []);

    const fetchBranches = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/getBranches`);
            const branchOptions = response.data.branches.map((b) => ({
                label: b.BRANCH_NAME,
                value: b.BRANCH_CODE
            }));
            setBranchList(branchOptions);
        } catch (error) {
            console.error('Failed to fetch branches', error);
            Swal.fire('Error', 'Failed to fetch branches', 'error');
        }
    };

    const fetchDealers = async (branchCode) => {
        try {
            const response = await axios.post(`${BASE_URL}/getDealers`, {
                branch_code: branchCode,
                login_id: login_id,
                
            });

            const dealerOptions = response.data.dealers.map((d) => ({
                label: d.Dealer_code,
                value: d.Dealer_code
            }));

            setDealerList(dealerOptions);
        } catch (error) {
            console.error('Failed to fetch dealers', error);
            Swal.fire('Error', 'Failed to fetch dealers', 'error');
        }
    };

    const handleView = async () => {
        if (!reportType || !selectedBranch || !selectedDealer ||
            (reportType.value === 'A' && (!month || !year))) {
            Swal.fire('Validation Error', 'Please select all required fields', 'warning');
            return;
        }

        setLoading(true);
        setShowTable(false);

        try {
            const res = await axios.post(`${BASE_URL}/getServiceClaims`, {
                reportType: reportType?.value,
                branch: selectedBranch?.value,
                dealer: selectedDealer?.value || null,
                month: reportType.value === 'A' ? month?.value : null,
                year: reportType.value === 'A' ? year?.value : null
            });

            if (res.data.claims.length === 0) {
                Swal.fire('No Data', 'No records found for the selected criteria', 'info');
            } else {
                setData(res.data.claims);
                setShowTable(true);
            }

        } catch (error) {
            console.error('Error fetching report', error);
            Swal.fire('Error', 'Failed to fetch data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return '#000000 !important';
        const normalizedStatus = String(status).trim().toLowerCase();
        
        switch (normalizedStatus) {
            case 'accounted': 
            case 'a': return '#4CAF50 !important'; 
            case 'pending': 
            case 'p': return '#FFC107 !important'; 
            case 'rejected': 
            case 'r': return '#F44336 !important'; 
            default: return '#000000 !important';  
        }
    };

    const exportToExcel = async () => {
        if (data.length === 0) {
            Swal.fire('No Data', 'No records to export', 'info');
            return;
        }

        const confirmResult = await Swal.fire({
            title: 'Confirm Export',
            text: 'Do you want to download the report as Excel?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Download',
            cancelButtonText: 'Cancel'
        });

        if (!confirmResult.isConfirmed) {
            return;  
        }

        Swal.fire({
            title: 'Exporting...',
            text: 'Please wait while the Excel file is being prepared.',
            icon: 'info',
            showConfirmButton: false,
            allowOutsideClick: false,
            willOpen: () => {
                Swal.showLoading();
            }
        });

        setTimeout(() => {
            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Service Claims");
            XLSX.writeFile(workbook, 'ServiceClaimReport.xlsx');

            Swal.close();
            Swal.fire('Success', 'Excel file exported successfully', 'success');
        }, 1500); 
    };
    


    return (
        <Box sx={{ p: 3 }}>
            {/* <Typography
                variant="h4"
                sx={{
                    color: '#2E86C1',
                    mb: 3,
                    textAlign: 'left'
                }}
            >
                Service Claim Report
            </Typography> */}


            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>

                        <Autocomplete
                            options={reportTypes}
                            getOptionLabel={(option) => option.label}
                            value={reportType}
                            onChange={(e, newValue) => setReportType(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Report Type" variant="outlined" />
                            )}
                            sx={{ width: 200 }}
                        />

                        <Autocomplete
                            options={branchList}
                            getOptionLabel={(option) => option.label}
                            value={selectedBranch}
                            onChange={(e, newValue) => {
                                setSelectedBranch(newValue);
                                fetchDealers(newValue?.value);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Branch" variant="outlined" />
                            )}
                            sx={{ width: 200 }}
                        />

                        <Autocomplete
                            options={dealerList}
                            getOptionLabel={(option) => option.label}
                            value={selectedDealer}
                            onChange={(e, newValue) => setSelectedDealer(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Dealer" variant="outlined" />
                            )}
                            sx={{ width: 200 }}
                        />

                        {reportType?.value === 'A' && (
                            <>
                                <Autocomplete
                                    options={months}
                                    getOptionLabel={(option) => option.label}
                                    value={month}
                                    onChange={(e, newValue) => setMonth(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Month" variant="outlined" />
                                    )}
                                    sx={{ width: 150 }}
                                />

                                <Autocomplete
                                    options={years}
                                    getOptionLabel={(option) => option.label}
                                    value={year}
                                    onChange={(e, newValue) => setYear(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Year" variant="outlined" />
                                    )}
                                    sx={{ width: 150 }}
                                />
                            </>
                        )}

<Button
    variant="contained"
    color="primary"
    onClick={handleView}
    sx={{ height: 56 }}
    startIcon={<VisibilityIcon />}  
>
    View
</Button>
<Button
    variant="contained"
    color="success"
    onClick={exportToExcel}
    sx={{ height: 56 }}
    startIcon={<DownloadIcon />}  
>
    Export to Excel
</Button>
                    </Box>

                    {loading && <CircularProgress />}

                    {showTable && (
                        <TableContainer component={Paper} sx={{ maxHeight: 500, overflowY: 'auto' }}>
                            <Table stickyHeader>
                            <TableHead>
    <TableRow>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Mat Code</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Doc No</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Branch Code</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Serial No</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>SCR No</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Total Amount</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Claim From</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>Status</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>OLTPS GJV Number</TableCell>
        <TableCell sx={{ backgroundColor: '#ADD8E6', color: 'black', fontWeight: 'bold' }}>OLTPS GJV Date</TableCell>
    </TableRow>
</TableHead>

                                <TableBody>
                                    {data.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{item.MAT_CODE}</TableCell>
                                            <TableCell>{item.DOC_NO}</TableCell>
                                            <TableCell>{item.BRANCH_CODE}</TableCell>
                                            <TableCell>{item.SERIAL_NO}</TableCell>
                                            <TableCell>{item.SCR_NO}</TableCell>
                                            <TableCell>{item.TOTALAMOUNT}</TableCell>
                                            <TableCell>{item.CLAIMFROM}</TableCell>
                                            <TableCell>
    <span style={{ 
        color: getStatusColor(item.STATUS).replace(' !important', ''),
        fontWeight: 'bold'
    }}>
        {item.STATUS}
    </span>
</TableCell>
                                            <TableCell>{item.OLTPS_GJV_NUMBER}</TableCell>
                                            <TableCell>{item.OLTPS_GJV_DATE}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>


                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ServiceClaimReport;
